/**
 * Sprint 31, story-158 (docs/ux/i18n-house-style.md §11.3). Shared, tested list-conjunction
 * helper. Spanish joins a two-item (or longer) list with `y`, except when the *last* item starts
 * with the sound `i` (spelled `i-` or `hi-`, but not the diphthong `hie-`) — then `e`. Standard
 * Spanish orthography, the same shape as English "a" vs "an" — not a house-style invention.
 * English's own list conjunction ("and") never changes, so both conjunction words are supplied
 * by the caller (from the active locale's catalog) rather than hardcoded here — this keeps the
 * helper correct for whichever locale is active instead of silently assuming Spanish.
 *
 * This genuinely is control flow (the check has to run on the final, already-translated string),
 * so unlike the `xOne`/`xOther` catalog-key shape this is legitimately code — but it belongs in
 * exactly one shared, tested place, not copy-pasted at each call site that currently hardcodes
 * `' and '`/`', '`.
 *
 * Mirrored on mobile at `redesign/src/utils/i18nGrammar.utils.ts` — keep the two in sync.
 */

// `hie-` is a diphthong, not the `i`-sound this rule targets (e.g. `hielo` still takes `y`).
const STARTS_WITH_I_SOUND_RE = /^(hi(?!e)|i)/i;

/**
 * Joins already-translated list items into prose. `[a]` -> `a`; `[a, b]` -> `a {conjunction} b`
 * (or `a {conjunctionBeforeI} b` if `b` starts with the `i`/`hi` sound per §11.3); `[a, b, c]` ->
 * `a, b {conjunction} c`. For English, pass the same word (`'and'`) for both parameters — the
 * `i`/`hi` alternation is a Spanish-only rule.
 */
export function joinWithY(items: string[], conjunction: string, conjunctionBeforeI: string): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  const last = items[items.length - 1];
  const word = STARTS_WITH_I_SOUND_RE.test(last.trim()) ? conjunctionBeforeI : conjunction;
  const head = items.slice(0, -1).join(', ');
  return `${head} ${word} ${last}`;
}
